import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { XmarkIcon } from '@dynatrace/strato-icons';

import { DateRangeSelector } from './DateRangeSelector.js';
import { DateTimeInput } from './DateTimeInput.js';
import * as styles from './DateTimeRangePicker.sty.js';
import { isExpressionTriggerKey } from './expression-trigger-helpers.js';
import { useDateTimeRangePicker } from './hooks/useDateTimeRangePicker.js';
import { onTimeframeCopyCapture } from './selection-copy-helpers.js';
import type {
  DateTimeRangePickerProps,
  DateTimeRangePickerRef,
} from './types.js';
import { DateRangeSelectorRef, DateTimeInputRef } from './types.js';
import { useSelectionInteractionHandlers } from './useSelectionInteractionHandlers.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { TimeErrorKey } from '../../core/time/shared.types.js';
import { useDateTimeRangePickerStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import { validate } from '../../core/time/validation.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { fieldTokens } from '../../styles/field.sty.js';
import { textStyle } from '../../styles/textStyle.sty.js';
import {
  clearIconButton,
  dateTimePicker,
} from '../date-time-picker/DateTimePicker.sty.js';
import { focusDateTimeInput } from '../date-time-picker/utils/focusDateTimeInput.js';
import { FormFieldMessageContext } from '../forms-core/contexts/FormFieldMessageContext.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { SpinButtonHandler } from '../spin-buttons/spin-button-group/types.js';

/**
 * Returns true if a given DateTimeInput element contains the active (focused)
 * element *or* the window selection intersects it.
 *
 * The focus-based check covers the common case where a single spin button is
 * focused – including empty (placeholder) spin buttons whose `selectAll()` is
 * a no-op and therefore leave the window selection collapsed.
 *
 * The selection-based check covers cross-input scenarios such as Ctrl+A or
 * drag-select spanning both "from" and "to" inputs.
 */
function isInputInSelectionOrFocused(
  ref: RefObject<DateTimeInputRef | null>,
): boolean {
  const element = ref.current?.element;
  if (!element) {
    return false;
  }

  if (element.contains(document.activeElement)) {
    return true;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return false;
  }
  const range = selection.getRangeAt(0);
  return range.intersectsNode(element);
}

function focusExpressionInputAtEnd(
  ref: RefObject<DateTimeInputRef | null>,
): void {
  const expressionElement = ref.current?.expressionInputRef;
  if (!expressionElement) {
    return;
  }

  expressionElement.focus();

  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(expressionElement);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

function getOffsetWithinElement(
  element: HTMLElement,
  container: Node,
  offset: number,
): number {
  const range = document.createRange();
  range.selectNodeContents(element);
  range.setEnd(container, offset);
  return range.toString().length;
}

/**@internal */
export const DateTimeRangePickerRoot = forwardRef<
  DateTimeRangePickerRef,
  Omit<DateTimeRangePickerProps, 'value' | 'defaultValue' | 'onChange'>
>((props, forwardedRef) => {
  const {
    id: propId,
    disabled: propDisabled,
    readOnly = false,
    name,
    style: consumerStyle,
    className: consumerClassName,
    required: propRequired,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    placeholder: placeholderProp,
    onBlur,
    ...remainingProps
  }: Omit<
    DateTimeRangePickerProps,
    'value' | 'defaultValue' | 'onChange'
  > = props;
  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);
  const ariaProps = useAriaLabelingProps(props);
  const COMPONENT_NAME = 'DateTimeRangePicker';

  const rootRef = useRef<HTMLDivElement>(null);
  const touchedContainerRef = useRef<HTMLDivElement>(null);
  const dateTimeToRef = useRef<DateTimeInputRef>(null);
  const dateRangeSelectorRef = useRef<DateRangeSelectorRef>(null);

  const [hasInputContent, setHasInputContent] = useState(false);

  const setValue = useDateTimeRangePickerStore(
    (state) => state.setDateTimeRangePickerTimeframe,
  );
  const value = useDateTimeRangePickerStore(
    (state) => state.dateTimeRangePickerTimeframe,
  );
  const dateTimeFromRef = useDateTimeRangePickerStore(
    (state) => state.dateTimeFromRef,
  );
  const precision = useDateTimeRangePickerStore((state) => state.precision);
  const min = useDateTimeRangePickerStore((state) => state.min);
  const max = useDateTimeRangePickerStore((state) => state.max);
  const formatString = useDateTimeRangePickerStore(
    (state) => state.formatString,
  );

  const { disabled, required, id, ariaDisabled } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const { placeholder, onPasteCapture, onUndoPasteKeyDown } =
    useDateTimeRangePicker({
      placeholder: placeholderProp,
      readOnly,
      dateTimeFromRef,
      dateTimeToRef,
    });

  const intl = useIntl();

  const {
    state: { showError },
    dispatch: formMessageDispatch,
  } = useFormFieldMessagesContext();

  // Track the last error key returned by the imperative validate() so we can
  // pass it as `formerError` on the next call and clean up stale messages.
  const lastValidationErrorRef = useRef<TimeErrorKey | undefined>();

  // Provide root and input element reference and functions to interact with the calendar overlay
  useImperativeHandle(
    forwardedRef,
    (): DateTimeRangePickerRef => ({
      element: rootRef.current,
      inputRef: {
        from: dateTimeFromRef.current?.inputRef,
        to: dateTimeToRef.current?.inputRef,
      },
      open: () => dateRangeSelectorRef.current?.open(),
      close: () => dateRangeSelectorRef.current?.close(),
      focus: () => dateTimeFromRef.current?.focus?.(),
      getCurrentValue: () => ({
        from: value?.from ?? null,
        to: value?.to ?? null,
      }),
      validate() {
        lastValidationErrorRef.current = validate(value, min, max, {
          formMessageDispatch,
          formerError: lastValidationErrorRef.current,
          precision,
          showError: true,
          checkOrder: true,
          ref: rootRef,
          expectedFormat: formatString,
          valueMode: 'dateTimePicker',
          required,
        });
        formMessageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    }),
    [
      value,
      min,
      max,
      formMessageDispatch,
      precision,
      formatString,
      required,
      dateTimeFromRef,
    ],
  );

  const { focusClassName } = useFocusRing({
    focusWithin: true,
    ignoreModality: true,
    isMinimal: false,
    disabled,
    color: showError ? 'critical' : 'neutral',
  });

  const dateTimeFromLabel = intl.formatMessage({
    defaultMessage: 'DateTime from',
    id: 'fMy6q/KKOqbvCA3H',
    description:
      'Textbox to enter a relative or absolute timeframe from value.',
  });

  const dateTimeToLabel = intl.formatMessage({
    defaultMessage: 'DateTime to',
    id: 'uuhhc3nEZptwtV2E',
    description: 'Textbox to enter a relative or absolute timeframe to value.',
  });
  const clearButtonLabel = intl.formatMessage({
    defaultMessage: 'Clear date and time range',
    id: 'zX4r/Y8gJL1C8+5e',
    description: 'Aria label for the DateTimeRangePicker clear button.',
  });
  const formFieldMessageContextValue = useMemo(
    () => ({
      state: {
        customMessages: [],
        formControlMessages: [],
        showError,
      },
    }),
    [showError],
  );

  const { onMouseDown, onKeyDown: handleSelectionKeyDown } =
    useSelectionInteractionHandlers(
      touchedContainerRef,
      useCallback(() => {
        focusDateTimeInput(null, dateTimeFromRef, dateTimeToRef);
      }, [dateTimeFromRef]),
      dateTimeFromRef,
      dateTimeToRef,
    );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (disabled || readOnly) {
        handleSelectionKeyDown(event);
        return;
      }

      const fromInputIsIso =
        dateTimeFromRef.current?.element?.getAttribute('data-valuetype') ===
        'iso8601';
      const toInputIsIso =
        dateTimeToRef.current?.element?.getAttribute('data-valuetype') ===
        'iso8601';

      const switchFromToExpression =
        isInputInSelectionOrFocused(dateTimeFromRef) && fromInputIsIso;
      const switchToToExpression =
        isInputInSelectionOrFocused(dateTimeToRef) && toInputIsIso;
      if (
        isExpressionTriggerKey(event) &&
        (switchFromToExpression || switchToToExpression)
      ) {
        const char = event.key;

        // The primary input receives the character and focus.
        // When both are selected, 'from' receives the character and focus.
        const primaryRef = switchFromToExpression
          ? dateTimeFromRef
          : dateTimeToRef;

        primaryRef.current?.setExpressionValue(char);

        requestAnimationFrame(() => {
          focusExpressionInputAtEnd(primaryRef);
        });

        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Handle regular character input when both inputs are selected
      if (
        isInputInSelectionOrFocused(dateTimeFromRef) &&
        isInputInSelectionOrFocused(dateTimeToRef) &&
        !fromInputIsIso &&
        !toInputIsIso &&
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        const char = event.key;
        const fromExpressionElement =
          dateTimeFromRef.current?.expressionInputRef;
        const toExpressionElement = dateTimeToRef.current?.expressionInputRef;
        const selection = window.getSelection();

        if (
          fromExpressionElement &&
          toExpressionElement &&
          selection &&
          selection.rangeCount > 0 &&
          !selection.isCollapsed
        ) {
          const range = selection.getRangeAt(0);
          const fromValue = fromExpressionElement.textContent ?? '';
          const toValue = toExpressionElement.textContent ?? '';

          const fromStart = fromExpressionElement.contains(range.startContainer)
            ? getOffsetWithinElement(
                fromExpressionElement,
                range.startContainer,
                range.startOffset,
              )
            : 0;
          const toEnd = toExpressionElement.contains(range.endContainer)
            ? getOffsetWithinElement(
                toExpressionElement,
                range.endContainer,
                range.endOffset,
              )
            : toValue.length;

          dateTimeFromRef.current?.setExpressionValue(
            `${fromValue.slice(0, fromStart)}${char}`,
          );
          dateTimeToRef.current?.setExpressionValue(toValue.slice(toEnd));

          requestAnimationFrame(() => {
            focusExpressionInputAtEnd(dateTimeFromRef);
          });

          event.preventDefault();
          event.stopPropagation();
          return;
        }

        // Replace the selected expression in "from" and keep "to" unchanged.
        dateTimeFromRef.current?.setExpressionValue(char);

        requestAnimationFrame(() => {
          focusExpressionInputAtEnd(dateTimeFromRef);
        });

        event.preventDefault();
        event.stopPropagation();
        return;
      }

      handleSelectionKeyDown(event);
    },
    [
      disabled,
      readOnly,
      handleSelectionKeyDown,
      dateTimeFromRef,
      dateTimeToRef,
      setValue,
    ],
  );

  // Move from the last "from" segment to the first "to" segment.
  const handleFromTrailingEdge = useCallback(() => {
    dateTimeToRef.current?.focus?.();
  }, []);

  // Move from the first "to" segment to the last "from" segment.
  const handleToLeadingEdge = useCallback(() => {
    dateTimeFromRef.current?.focusLast();
  }, [dateTimeFromRef]);

  // Carry trailing overflow from "from" into the first "to" segment.
  const handleFromTrailingOverflow = useCallback(
    (overflowChar: string) => {
      const handlers = Array.from(
        dateTimeToRef.current?.spinButtonHandlers?.values() ?? [],
      ).filter((h): h is SpinButtonHandler => h !== null && h !== undefined);

      const firstHandler = handlers[0];
      if (!firstHandler) {
        return;
      }

      if (value?.to?.type === 'expression' || value?.to === null) {
        dateTimeToRef.current?.setExpressionValue(overflowChar);
        requestAnimationFrame(() => {
          const exprEl = dateTimeToRef.current?.expressionInputRef;
          if (exprEl) {
            // Focus first so that expressionOnFocusHandler fires and selects all,
            // then immediately collapse the selection to the end so the cursor
            // lands after the overflow character instead of selecting everything.
            exprEl.focus();
            const sel = window.getSelection();
            if (sel) {
              const range = document.createRange();
              range.selectNodeContents(exprEl);
              range.collapse(false);
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }
        });
        return;
      }

      if (firstHandler.type === 'number') {
        firstHandler.applyText(overflowChar, true);
        firstHandler.element.current?.focus();
        firstHandler.setSelection({ start: 0, end: 0, rtl: true });
      }
    },
    [value?.to],
  );

  return (
    <Flex
      ref={rootRef}
      flexDirection="column"
      data-testid={dataTestId}
      style={consumerStyle}
      className={consumerClassName}
      {...ariaProps}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
    >
      <FormFieldMessageContext.Provider value={formFieldMessageContextValue}>
        <InputGroup style={{ gridTemplateColumns: '1fr auto' }}>
          <Flex
            ref={touchedContainerRef}
            className={clsx(
              inputGroupClassName,
              dateTimePicker({
                readOnly,
                showError,
                disabled,
                type: 'datetime',
              }),
              textStyle({ textStyle: 'base' }),
              fieldTokens({
                color: showError ? 'critical' : 'neutral',
                disabled: disabledOrAriaDisabled,
                variant: 'emphasized',
              }),
              focusClassName,
              styles.dateTimeRange({
                showError,
                disabled: disabledOrAriaDisabled,
                readOnly: readOnly,
              }),
            )}
            onCopyCapture={onTimeframeCopyCapture}
            onPasteCapture={onPasteCapture}
            onBlur={(event) => {
              // onFocus synchronously redirects focus from the container to a spin button,
              // causing a spurious onBlur; rAF defers the call until focus has settled.
              requestAnimationFrame(() => {
                onBlur?.(event);
              });
            }}
            onInput={() => {
              setHasInputContent(
                !!dateTimeFromRef.current?.expressionInputRef?.textContent ||
                  !!dateTimeToRef.current?.expressionInputRef?.textContent,
              );
            }}
            tabIndex={disabled ? undefined : 0}
            onMouseDown={onMouseDown}
            onKeyDownCapture={(e) => {
              // Handles undo for from and to inputs with Ctrl+Z.
              // Uses capture phase so it fires before the input-level undo handler.
              onUndoPasteKeyDown(e);
            }}
            onKeyDown={onKeyDown}
            onFocus={(e: FocusEvent) => {
              // Forward the focus only if it is coming from outside this component.
              // Focus the first spin button with textContent; otherwise, focus the input.

              if (!rootRef.current?.contains(e.relatedTarget)) {
                focusDateTimeInput(e, dateTimeFromRef, dateTimeToRef);
              }
            }}
          >
            <span className={styles.scrollContainer}>
              <DateTimeInput
                ref={dateTimeFromRef}
                className={styles.dateTimeInput}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={placeholder.from}
                name={name === undefined ? undefined : `${name}-from`}
                // Maintains its own state to separate single and range value manipulations
                value={value === null ? null : value.from}
                onChange={(newValue) => {
                  if (typeof newValue !== 'string') {
                    setValue({ from: newValue ?? null });
                  }
                }}
                aria-label={dateTimeFromLabel}
                aria-disabled={ariaDisabled}
                required={required}
                controlId={id}
                onTrailingEdge={handleFromTrailingEdge}
                onTrailingOverflow={handleFromTrailingOverflow}
                onPasteComplete={() => {
                  dateTimeToRef.current?.focus?.();
                }}
              />

              <span className={styles.timeframeSeparator}>→</span>
              <DateTimeInput
                ref={dateTimeToRef}
                className={styles.dateTimeInput}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={placeholder.to}
                name={name === undefined ? undefined : `${name}-to`}
                // Maintains its own state to separate single and range value manipulations
                value={value === null ? null : value.to}
                onChange={(newValue) => {
                  if (typeof newValue !== 'string') {
                    setValue({ to: newValue ?? null });
                  }
                }}
                aria-label={dateTimeToLabel}
                aria-disabled={ariaDisabled}
                required={required}
                onLeadingEdge={handleToLeadingEdge}
                dateOnlyPasteAnchor="endOfDay"
                onPasteComplete={() => {
                  requestAnimationFrame(() => {
                    dateTimeToRef.current?.focusLast();
                  });
                }}
              />
            </span>
            {!readOnly &&
              !disabledOrAriaDisabled &&
              ((value &&
                (value.from?.value !== '' || value.to?.value !== '')) ||
                hasInputContent) && (
                <Button
                  onFocus={() => {
                    formMessageDispatch?.({
                      type: FormFieldMessagesAction.SETISTOUCHED,
                      payload: { isTouched: true },
                    });
                  }}
                  onClick={() => {
                    setValue(null);
                    if (dateTimeFromRef.current?.expressionInputRef) {
                      dateTimeFromRef.current.expressionInputRef.textContent =
                        '';
                    }
                    if (dateTimeToRef.current?.expressionInputRef) {
                      dateTimeToRef.current.expressionInputRef.textContent = '';
                    }
                    setHasInputContent(false);
                    requestAnimationFrame(() => {
                      dateTimeFromRef.current?.expressionInputRef?.focus();
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                  className={clearIconButton}
                  color={showError ? 'critical' : 'neutral'}
                  size="condensed"
                  aria-label={clearButtonLabel}
                >
                  <XmarkIcon />
                </Button>
              )}
          </Flex>
          <DateRangeSelector
            ref={dateRangeSelectorRef}
            disabled={disabled}
            aria-disabled={ariaDisabled}
            readOnly={readOnly}
            error={showError}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
          />
        </InputGroup>
      </FormFieldMessageContext.Provider>
    </Flex>
  );
});
